import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Home, Phone, Search } from 'lucide-react';
import { authService } from '../../services/auth';

const STATUS_STYLES = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  confirmada: 'bg-green-100 text-green-800',
  cancelada: 'bg-red-100 text-red-800'
};

const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/appointments`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      setAppointments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando citas:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || '/api'}/appointments/${id}`, {
        method: 'PATCH',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ status })
      });
      setAppointments(prev => prev.map(a => (a.id === id ? { ...a, status } : a)));
    } catch (error) {
      console.error('Error actualizando cita:', error);
    }
  };

  const filteredAppointments = appointments.filter(a =>
    `${a.name} ${a.email} ${a.propertyTitle}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Citas</h1>
        <p className="text-gray-600">Visitas agendadas desde la web</p>
      </div>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nombre, email o propiedad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {filteredAppointments.map((appointment) => (
          <div key={appointment.id} className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h3 className="font-semibold text-gray-800">{appointment.name}</h3>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_STYLES[appointment.status] || STATUS_STYLES.pendiente}`}>
                    {appointment.status}
                  </span>
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-gray-400" />
                    {appointment.propertyTitle || 'Sin propiedad asociada'}
                  </p>
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {appointment.date}
                    <Clock className="w-4 h-4 text-gray-400 ml-2" />
                    {appointment.time}
                  </p>
                  <p className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    {appointment.email}
                  </p>
                  {appointment.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {appointment.phone}
                    </p>
                  )}
                  {appointment.notes && (
                    <p className="text-gray-700 mt-2">{appointment.notes}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <select
                  value={appointment.status}
                  onChange={(e) => updateStatus(appointment.id, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {filteredAppointments.length === 0 && (
          <div className="text-center py-16">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm ? 'No hay citas que coincidan con la búsqueda' : 'Todavía no hay visitas agendadas'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentsPage;
